import { createContext } from "react";

// Product routing interface
export interface MerchantRoutes {
  // Home routes
  overviewHref: string;
  homeBaseHref: string;
  analyticsHref: string;
  getAnalyticsSubRoute: (subPath: string) => string;

  // Shipping routes
  shippingDefaultRoute: string;
  ordersOutboundLabelsHref: string;
  getOrdersOutboundLabelsSubRoute: (subPath: string) => string;
  pickSessionHref: string;
  pickSessionTableHref: string;
  transferHref: string;
  instantTransferHref: string;
  receiveToBinHref: string;
  consolidationHref: string;
  warehouseHref: string;
  warehouseBaseUrl: string;
  tariffCalculatorHref: string;
  orderEditsHref: string;
  classifyProductsHref: string;
  analyticsOrderFulfillmentHref: string;
  getAnalyticsOrderFulfillmentSubRoute: (subPath: string) => string;
  shippingCarrierClaimsHref: string;

  // Returns routes
  returnsDefaultRoute: string;
  returnsHref: string;
  getReturnsSubRoute: (subPath: string) => string;
  claimsHref: string;
  claimsDefaultRoute: string;
  getClaimsSubRoute: (subPath: string) => string;

  getWarrantiesHref: ({ enabled }: { enabled: boolean }) => string;
  warrantiesHref: string;
  getWarrantiesSubRoute: (subPath: string) => string;

  registrationsHref: string;
  returnsProcessingHref: string;

  getRecycleHref: () => string;
  recycleHref: string;
  getRecycleSubRoute: (subPath: string) => string;

  analyticsReturnsClaimsHref: string;
  getAnalyticsReturnsClaimsSubRoute: (subPath: string) => string;
  analyticsReportsHref: string;
  analyticsReturnsProcessingHref: string;
  analyticsManagedReturnsHref: string;
  warehouseSlasHref: string;

  // Inventory routes
  getInventoryHref: ({ enabled }: { enabled: boolean }) => string;
  inventoryProductsHref: string;

  // Marketing routes
  marketingAnalyticsHref: string;
  automationsHref: string;
  getSegmentBuilderRoute({
    teamId,
    segmentId,
  }: {
    teamId: string;
    segmentId: string;
  }): string;
  getTrackingHref: ({ enabled }: { enabled: boolean }) => string;
  getTrackingSubRoute: (subPath: string) => string;
  recoverHref: string;
  getRecoverSubRoute: (subPath: string) => string;
  contextualHref: string;
  getContextualSubRoute: (subPath: string) => string;
  reviewsHref: string;
  getReviewsSubRoute: (subPath: string) => string;
  emailSmsHref: string;
  getEmailSmsSubRoute: (subPath: string) => string;
  loyaltyHref: string;
  getLoyaltySubRoute: (subPath: string) => string;
  marketingAgentHref: string;
  getMarketingAgentSubRoute: (subPath: string) => string;
  textConciergeHref: string;
  getTextConciergeSubRoute: (subPath: string) => string;

  // Support routes
  getSupportHref: ({ enabled }: { enabled: boolean }) => string;
  getSupportTableHref: (viewName: string) => string;
  getSupportTableSubRoute: (subPath: string) => string;
  getConciergeHref: ({ enabled }: { enabled: boolean }) => string;
  getConciergeSubRoute: (subPath: string) => string;
  getSupportDevSubRoute: (subPath: string) => string;

  // Conversion routes
  spotlightsHref: string;
  getSpotlightsSubRoute: (subPath: string) => string;
  conversionAnalyticsHref: string;
  getConversionHref: ({
    enabled,
    checkoutOutboundRatesEnabled,
  }: {
    enabled: boolean;
    checkoutOutboundRatesEnabled: boolean;
  }) => string;
  getConversionSubRoute: (subPath: string) => string;

  // Marketing routes
  getMarketingHref: ({
    emailSmsEnabled,
    marketingCloudEnabled,
  }: {
    emailSmsEnabled: boolean;
    marketingCloudEnabled: boolean;
  }) => string;

  // Customers routes
  getCustomerRecordHref: ({ customerId }: { customerId?: string }) => string;

  // Settings routes
  settingsHref: string;
  getSettingsSubRoute: (subPath: string) => string;

  // Customer portal builder route
  customerPortalBuilderHref: string;

  // Base URLs for sections
  shippingBaseUrl: string;
  returnsBaseUrl: string;
  inventoryManagementBaseUrl: string;
  marketingBaseUrl: string;
  supportBaseUrl: string;
  conversionBaseUrl: string;
  customersBaseUrl: string;
  storeBaseUrl: string;
  aeoBaseUrl: string;

  // Finance routes
  financeBaseUrl: string;
  disputesHref: string;
  disputesDashboardHref: string;
  disputesChargebacksHref: string;
  disputesSettingsHref: string;
  disputesIntegrationsHref: string;

  // Redo Supplies routes
  redoSuppliesBaseUrl: string;
  redoSuppliesShopHref: string;
  redoSuppliesOrderHistoryHref: string;
  redoSuppliesRecurringPurchasesInventoryHref: string;
  redoSuppliesRecurringPurchasesTimeHref: string;

  // AEO routes
  aeoDashboardHref: string;
  aeoIntegrationsHref: string;
  aeoPromptsHref: string;
  aeoCatalogHref: string;
  aeoAssistantHref: string;
  aeoCompetitorsHref: string;
  aeoBrandHref: string;
  getAeoSubRoute: (subPath: string) => string;
  getAeoCatalogSubRoute: (subPath: string) => string;
}

export const MerchantRoutesContext = createContext<MerchantRoutes | undefined>(
  undefined,
);
